import { useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { Badge } from '@upme/ui/components/badge';
import { Button } from '@upme/ui/components/button';
import { RefreshCw } from 'lucide-react';
import { useCallback, useRef, useState } from 'react';
import { toast } from 'sonner';
import { listCitiesOptions } from '../../../features/cities/queries';
import { listEventCategoriesOptions } from '../../../features/event-categories/queries';
import { ImportQueueModerationActions } from '../../../features/import-queue/components/import-queue-moderation-actions';
import {
  PublishFromImportForm,
  type PublishFromImportFormApi,
} from '../../../features/import-queue/components/publish-from-import-form';
import { RawPostPreview } from '../../../features/import-queue/components/raw-post-preview';
import {
  formatAutoPublishSkipReasonDetail,
  formatConfidence,
  formatSimilarity,
} from '../../../features/import-queue/lib/format';
import { useReextractImportItem } from '../../../features/import-queue/mutations';
import { importQueueItemByIdOptions } from '../../../features/import-queue/queries';
import { PageHeader } from '../../../shared/components/page-header';

export const Route = createFileRoute('/_authenticated/import-queue/$messageId')({
  staticData: { breadcrumb: 'Сообщение' },
  loader: async ({ context, params }) => {
    await Promise.all([
      context.queryClient.ensureQueryData(importQueueItemByIdOptions(params.messageId)),
      context.queryClient.ensureQueryData(listCitiesOptions()),
      context.queryClient.ensureQueryData(listEventCategoriesOptions()),
    ]);
  },
  component: ImportQueueDetailPage,
});

const confidenceClass: Record<'high' | 'mid' | 'low' | 'none', string> = {
  high: 'border-emerald-500 text-emerald-700 dark:text-emerald-400',
  mid: 'border-amber-500 text-amber-700 dark:text-amber-400',
  low: 'border-rose-500 text-rose-700 dark:text-rose-400',
  none: '',
};

const eventStatusClass: Record<'DRAFT' | 'PENDING_MODERATION' | 'PUBLISHED' | 'ARCHIVED', string> = {
  DRAFT: '',
  PENDING_MODERATION: 'border-amber-500 text-amber-700 dark:text-amber-400',
  PUBLISHED: 'border-emerald-500 text-emerald-700 dark:text-emerald-400',
  ARCHIVED: '',
};

function ImportQueueDetailPage() {
  const { messageId } = Route.useParams();
  const { data: item } = useSuspenseQuery(importQueueItemByIdOptions(messageId));
  const { data: cities } = useSuspenseQuery(listCitiesOptions());
  const { data: categories } = useSuspenseQuery(listEventCategoriesOptions());

  const [publishApi, setPublishApi] = useState<PublishFromImportFormApi | null>(null);
  const handlePublishReady = useCallback((api: PublishFromImportFormApi) => {
    setPublishApi(api);
  }, []);

  // Сброс publishApi при переходе на другое сообщение делаем в фазе рендера, а НЕ
  // в useEffect: mount-эффект дочерней формы (onPublishReady) выполняется раньше
  // эффекта родителя, и родительский setPublishApi(null) затирал бы только что
  // переданный api → publishApi навсегда оставался null и все кнопки публикации
  // были disabled. Форма всё равно ремаунтится по key={messageId}, поэтому здесь
  // достаточно синхронно обнулить стейт при реальной смене id.
  const prevMessageId = useRef(messageId);
  if (prevMessageId.current !== messageId) {
    prevMessageId.current = messageId;
    setPublishApi(null);
  }

  const city = cities.find((c) => c.id === item.channel.cityId);

  const reextract = useReextractImportItem(item.id);

  const handleReextract = async () => {
    try {
      await reextract.mutateAsync();
      toast.success('Запущена повторная экстракция');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Не удалось перезапустить');
    }
  };

  const confidence = formatConfidence(item.extracted?.confidence);

  const reextractButton = (
    <Button onClick={handleReextract} disabled={reextract.isPending} variant="outline" size="sm">
      <RefreshCw className="h-4 w-4 mr-2" />
      Re-extract
    </Button>
  );

  return (
    <div className="space-y-6">
      <PageHeader
        title={item.extracted?.fields.title ?? `Сообщение @${item.channel.slug}`}
        description={
          <span className="text-sm text-muted-foreground">
            Канал{' '}
            <Link
              to="/source-channels/$channelId"
              params={{ channelId: item.channel.id }}
              className="hover:underline"
            >
              {item.channel.title}
            </Link>{' '}
            · #{item.telegramMessageId}
          </span>
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <RawPostPreview message={item} city={city} />

        <div className="space-y-4">
          {item.status === 'EXTRACTED' && (
            <>
              <div className="rounded-md border bg-card p-4 space-y-2 text-sm">
                <div className="font-semibold">LLM</div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <div className="text-xs text-muted-foreground">Модель</div>
                    <div className="font-mono text-xs">{item.llm.model ?? '—'}</div>
                  </div>
                  <div>
                    <div className="text-xs text-muted-foreground">Попыток</div>
                    <div>{item.llm.attempts}</div>
                  </div>
                  <div>
                    <div className="text-xs text-muted-foreground">Стоимость, $</div>
                    <div className="tabular-nums">
                      {item.llm.costUsd != null ? item.llm.costUsd.toFixed(4) : '—'}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-muted-foreground">Уверенность</div>
                    <div>
                      {confidence.tone === 'none' ? (
                        <span className="text-muted-foreground">—</span>
                      ) : (
                        <Badge variant="outline" className={confidenceClass[confidence.tone]}>
                          {confidence.label}
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>
                {item.autoPublishSkipReason &&
                  (() => {
                    const detail = formatAutoPublishSkipReasonDetail(item.autoPublishSkipReason);
                    return detail ? (
                      <div className="rounded border border-amber-500/40 bg-amber-50 dark:bg-amber-950/20 p-3 space-y-1">
                        <div className="font-medium text-amber-900 dark:text-amber-200">
                          Не авто-опубликовано: {detail.title}
                        </div>
                        <div className="text-sm text-amber-900/80 dark:text-amber-200/80">
                          {detail.description}
                        </div>
                        <div className="text-sm text-amber-900/60 dark:text-amber-200/60">{detail.hint}</div>
                      </div>
                    ) : null;
                  })()}
                {item.extracted?.reason && (
                  <div>
                    <div className="text-xs text-muted-foreground mt-2">Reason</div>
                    <div className="text-xs italic">{item.extracted.reason}</div>
                  </div>
                )}
              </div>

              {(item.possibleDuplicates?.length ?? 0) > 0 && (
                <div className="rounded-md border border-violet-500/40 bg-violet-50 dark:bg-violet-950/20 p-3 space-y-2 text-sm">
                  <div className="font-medium text-violet-900 dark:text-violet-200">
                    Возможные дубли ({item.possibleDuplicates?.length})
                  </div>
                  <ul className="space-y-1">
                    {item.possibleDuplicates?.map((dup) => (
                      <li key={dup.id} className="flex items-center gap-2">
                        <Link
                          to="/events/$eventId"
                          params={{ eventId: dup.id }}
                          className="hover:underline font-medium text-violet-800 dark:text-violet-300"
                        >
                          {dup.title}
                        </Link>
                        {dup.startsAt && (
                          <span className="text-xs text-muted-foreground">
                            {new Date(dup.startsAt).toLocaleDateString('ru-RU')}
                          </span>
                        )}
                        <Badge
                          variant="outline"
                          className="border-violet-500 text-violet-700 dark:text-violet-400"
                        >
                          схожесть{formatSimilarity(dup.similarity)}
                        </Badge>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <PublishFromImportForm
                key={messageId}
                item={item}
                cities={cities}
                categories={categories}
                onPublishReady={handlePublishReady}
              />

              <ImportQueueModerationActions item={item} categories={categories} publishApi={publishApi} />
            </>
          )}

          {item.status === 'IMPORTED' && item.events.length > 0 && (
            <>
              <div className="rounded-md border bg-card p-4 space-y-2">
                <div className="text-xs text-muted-foreground">
                  {item.events.length === 1
                    ? 'Опубликовано как событие'
                    : `Создано событий: ${item.events.length}`}
                </div>
                <div className="flex flex-wrap gap-2">
                  {item.events.map((ev) => (
                    <Link
                      key={ev.id}
                      to="/events/$eventId"
                      params={{ eventId: ev.id }}
                      className="inline-flex items-center gap-2 rounded-md border px-3 py-1.5 hover:bg-muted"
                    >
                      <span className="font-medium">{ev.title}</span>
                      <span className="text-xs text-muted-foreground">
                        {new Date(ev.startsAt).toLocaleDateString('ru-RU')}
                      </span>
                      <Badge variant="outline" className={eventStatusClass[ev.status]}>
                        {ev.status}
                      </Badge>
                    </Link>
                  ))}
                </div>
                {item.reviewerNote && (
                  <div className="text-xs text-muted-foreground">Заметка модератора: {item.reviewerNote}</div>
                )}
              </div>
              <div className="flex flex-wrap gap-2">{reextractButton}</div>
            </>
          )}

          {item.status === 'REJECTED' && (
            <>
              <div className="rounded-md border border-destructive/40 bg-destructive/5 p-4 space-y-2">
                <div className="font-semibold text-destructive">Отклонено</div>
                {item.reviewerNote ? (
                  <div className="text-sm">{item.reviewerNote}</div>
                ) : (
                  <div className="text-xs text-muted-foreground italic">Без комментария</div>
                )}
              </div>
              <div className="flex flex-wrap gap-2">{reextractButton}</div>
            </>
          )}

          {item.status === 'PENDING' && (
            <>
              <div className="rounded-md border bg-muted/40 p-4 space-y-2 text-sm">
                <div className="font-semibold">Извлечение ещё не завершено или провалилось.</div>
                {item.llm.lastError && (
                  <div>
                    <div className="text-xs text-muted-foreground mt-1">Последняя ошибка</div>
                    <div className="font-mono text-xs break-all">{item.llm.lastError}</div>
                  </div>
                )}
                <div className="text-xs text-muted-foreground">Попыток: {item.llm.attempts}</div>
              </div>
              <div className="flex flex-wrap gap-2">{reextractButton}</div>
            </>
          )}

          {item.status === 'IGNORED' && (
            <>
              <div className="rounded-md border bg-muted/40 p-4 space-y-2 text-sm">
                <div className="font-semibold">Игнорировано</div>
                {item.reviewerNote && <div className="text-xs">{item.reviewerNote}</div>}
              </div>
              <div className="flex flex-wrap gap-2">{reextractButton}</div>
            </>
          )}

          {item.status === 'DUPLICATE' && (
            <>
              <div className="rounded-md border border-violet-500/40 bg-violet-50 dark:bg-violet-950/20 p-4 space-y-3">
                <div className="font-semibold text-violet-900 dark:text-violet-200">Дубль</div>
                {item.duplicate != null ? (
                  <div className="space-y-2 text-sm">
                    {item.duplicate.ofEventId && (
                      <div className="flex items-center gap-2">
                        <span className="text-muted-foreground">Событие:</span>
                        <Link
                          to="/events/$eventId"
                          params={{ eventId: item.duplicate.ofEventId }}
                          className="hover:underline font-medium text-violet-800 dark:text-violet-300"
                        >
                          Перейти к событию
                        </Link>
                        {item.duplicate.similarity != null && (
                          <Badge
                            variant="outline"
                            className="border-violet-500 text-violet-700 dark:text-violet-400"
                          >
                            схожесть{formatSimilarity(item.duplicate.similarity)}
                          </Badge>
                        )}
                      </div>
                    )}
                    {item.duplicate.ofNewsId && (
                      <div className="flex items-center gap-2">
                        <span className="text-muted-foreground">Новость:</span>
                        <span className="font-mono text-xs">{item.duplicate.ofNewsId}</span>
                        {item.duplicate.similarity != null && (
                          <Badge
                            variant="outline"
                            className="border-violet-500 text-violet-700 dark:text-violet-400"
                          >
                            схожесть{formatSimilarity(item.duplicate.similarity)}
                          </Badge>
                        )}
                      </div>
                    )}
                    {!item.duplicate.ofEventId && !item.duplicate.ofNewsId && (
                      <div className="text-xs text-muted-foreground">Канонический объект не указан.</div>
                    )}
                  </div>
                ) : (
                  <div className="text-xs text-muted-foreground">Информация о дубле не сохранена.</div>
                )}
              </div>
              <div className="flex flex-wrap gap-2">{reextractButton}</div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
